/**
 * IncidentChecks (13.2-08, D-13.2-P-8): an incident's verification checklist (`incidentChecklist`, 13.2-06), drawn
 * the same way on Verify's "Incidents to close" and on the record's closure panel, so Oversight and the Care Manager
 * see one list.
 *
 * A passing check is a quiet tick and its label. A failing one reads amber, with its detail (for example "Not recorded:
 * SIRS Priority 1 notice (SirsNoticeAt), due 23 Oct 2026, 3:40 pm, passed."). Amber, never red: an open check is a
 * thing to do, not a failure (the house rule, record.module.css).
 */

import type { CheckItem } from '../../lib/incidentVerify';
import styles from './IncidentChecks.module.css';

export interface IncidentChecksProps {
  items: readonly CheckItem[];
  /** The list's accessible name. */
  label?: string;
}

export default function IncidentChecks({ items, label = 'Verification checks' }: IncidentChecksProps) {
  return (
    <ul className={styles.checks} aria-label={label}>
      {items.map((item) => (
        <li key={item.key} className={item.ok ? styles.ok : styles.open} data-check={item.key} data-ok={item.ok ? 'yes' : 'no'}>
          <span className={styles.mark} aria-hidden="true">
            {item.ok ? (
              <svg viewBox="0 0 16 16" focusable="false">
                <path d="M3.5 8.5l3 3 6-7" />
              </svg>
            ) : (
              <svg viewBox="0 0 16 16" focusable="false">
                <circle cx="8" cy="8" r="5.5" />
              </svg>
            )}
          </span>
          <span className={styles.body}>
            <span className={styles.label}>
              <span className={styles.sr}>{item.ok ? 'Done: ' : 'Open: '}</span>
              {item.label}
            </span>
            {!item.ok && <span className={styles.detail}>{item.detail}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}
